import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { ConditionalEffect } from '../types/conditional-effect';
import type { CreateTemplateConditionalEffectPayload } from '../types/create-template-conditional-effect-payload';
import { ConditionOperator } from '../types/condition-operator';
import type {
  ConditionalContentRowSelection,
  ConditionalContentSectionSelection,
  ConditionalContentSelection,
} from '../types/selection-payload';

import type { ConditionGroupInput } from '../types/condition-group-input';
import type { EffectInput, EffectPatch } from '../types/effect-input';
import type {
  EditorNodeUpdateEffect,
  EditorRowUpdateEffect,
  EditorSectionUpdateEffect,
  Effect,
} from '../types/effect';
import { isNodeSelection, isRowSelection, isSectionSelection } from './type.helper';

export class EditorConditionsHelper {
  static findConditionalEffectBySelection = (
    selection: ConditionalContentSelection,
    conditionalEffects: ConditionalEffect[]
  ): ConditionalEffect => {
    if (conditionalEffects?.length === 0) return null;

    if (!selection) {
      return null;
    }
    if (isNodeSelection(selection)) {
      return conditionalEffects.find(
        effect =>
          effect.truthyEffects.some(
            truthyEffect =>
              truthyEffect.objectType === 'EditorNode' && truthyEffect.nodeId === selection.nodeId
          ) ||
          effect.falsyEffects.some(
            truthyEffect =>
              truthyEffect.objectType === 'EditorNode' && truthyEffect.nodeId === selection.nodeId
          )
      );
    }
    if (isRowSelection(selection)) {
      return conditionalEffects.find(
        effect =>
          effect.truthyEffects.some(
            truthyEffect =>
              truthyEffect.objectType === 'EditorRow' && truthyEffect.rowId === selection.rowId
          ) ||
          effect.falsyEffects.some(
            truthyEffect =>
              truthyEffect.objectType === 'EditorRow' && truthyEffect.rowId === selection.rowId
          )
      );
    }
    if (isSectionSelection(selection)) {
      return conditionalEffects.find(
        effect =>
          effect.truthyEffects.some(
            truthyEffect =>
              truthyEffect.objectType === 'EditorSection' &&
              truthyEffect.sectionId === selection.sectionId
          ) ||
          effect.falsyEffects.some(
            truthyEffect =>
              truthyEffect.objectType === 'EditorSection' &&
              truthyEffect.sectionId === selection.sectionId
          )
      );
    }
  };

  static findEffectBySelection = (selection: ConditionalContentSelection, effects: Effect[]) => {
    if (effects?.length === 0) return null;

    if (!selection) {
      return null;
    }
    if (isNodeSelection(selection)) {
      return effects.find(
        effect => effect.objectType === 'EditorNode' && effect.nodeId === selection.nodeId
      ) as EditorNodeUpdateEffect;
    }
    if (isRowSelection(selection)) {
      return effects.find(
        effect => effect.objectType === 'EditorRow' && effect.rowId === selection.rowId
      ) as EditorRowUpdateEffect;
    }
    if (isSectionSelection(selection)) {
      return effects.find(
        effect => effect.objectType === 'EditorSection' && effect.sectionId === selection.sectionId
      ) as EditorSectionUpdateEffect;
    }
  };

  static createEmptyConditionGroup = (): ConditionGroupInput => ({
    conditions: [
      {
        operator: ConditionOperator.equal,
      },
    ],
  });

  static createNodeConditionalEffect = ({
    contentId,
    sectionId,
    rowId,
    cellId,
    nodeId,
    templateId,
  }: {
    contentId: string;
    sectionId: string;
    rowId: string;
    cellId: string;
    nodeId: string;
    templateId: string;
  }): CreateTemplateConditionalEffectPayload => ({
    name: 'Conditional content',
    description: '',
    conditionGroups: [
      {
        conditions: [
          {
            operator: ConditionOperator.equal,
          },
        ],
      },
    ],
    truthyEffects: [
      {
        editorNodeUpdateEffect: {
          nodeId,
          rowId,
          sectionId,
          contentId,
          cellId,
          payload: {
            hidden: false,
          },
        },
      },
    ],
    falsyEffects: [
      {
        editorNodeUpdateEffect: {
          nodeId,
          rowId,
          sectionId,
          contentId,
          cellId,
          payload: {
            hidden: true,
          },
        },
      },
    ],
    templateId,
  });

  static createEmptyConditionalEffect = ({
    templateId,
  }: {
    templateId: string;
  }): CreateTemplateConditionalEffectPayload => ({
    name: '',
    description: '',
    conditionGroups: [
      {
        conditions: [
          {
            operator: ConditionOperator.equal,
          },
        ],
      },
    ],
    truthyEffects: [],
    falsyEffects: [],
    templateId,
  });

  static createRowConditionalEffect = ({
    rowId,
    sectionId,
    contentId,
  }: ConditionalContentRowSelection): {
    falsyEffects: EffectInput[];
    truthyEffects: EffectInput[];
  } => ({
    truthyEffects: [
      {
        editorRowUpdateEffect: {
          rowId,
          sectionId,
          contentId,
          payload: {
            hidden: false,
          },
        },
      },
    ],
    falsyEffects: [
      {
        editorRowUpdateEffect: {
          rowId,
          sectionId,
          contentId,
          payload: {
            hidden: true,
          },
        },
      },
    ],
  });

  static createSectionConditionalEffect = ({
    sectionId,
    contentId,
  }: ConditionalContentSectionSelection): {
    falsyEffects: EffectInput[];
    truthyEffects: EffectInput[];
  } => ({
    truthyEffects: [
      {
        editorSectionUpdateEffect: {
          sectionId,
          contentId,
          payload: {
            hidden: false,
          },
        },
      },
    ],
    falsyEffects: [
      {
        editorSectionUpdateEffect: {
          sectionId,
          contentId,
          payload: {
            hidden: true,
          },
        },
      },
    ],
  });

  static createEffectFromSelection = (
    conditionalContentSelection: ConditionalContentSelection
  ): {
    falsyEffects: EffectInput[];
    truthyEffects: EffectInput[];
  } => {
    if (isRowSelection(conditionalContentSelection)) {
      return EditorConditionsHelper.createRowConditionalEffect(conditionalContentSelection);
    }
    if (isSectionSelection(conditionalContentSelection)) {
      return EditorConditionsHelper.createSectionConditionalEffect(conditionalContentSelection);
    }
  };

  static getTitleFromEffect = (
    effect: EffectInput | EffectPatch,
    pages: DocumentPage[]
  ): string => {
    if (effect.editorRowUpdateEffect) {
      const page = pages.find(
        page => page.editorBlock?.contentId === effect.editorRowUpdateEffect.contentId
      );
      const section = page?.editorBlock?.content.sections.find(
        section => section.id === effect.editorRowUpdateEffect.sectionId
      );
      const row = section?.rows.find(row => row.id === effect.editorRowUpdateEffect.rowId);

      if (!row) {
        return t('conditional_logic_no_content_found');
      }
      return t('conditional_logic_selected_editor_row_title', {
        page: page.pageNum,
        element: section.rows.indexOf(row) + 1,
      });
    }
    if (effect.editorSectionUpdateEffect) {
      const page = pages.find(
        page => page.editorBlock?.contentId === effect.editorSectionUpdateEffect.contentId
      );

      if (!page) {
        return t('conditional_logic_no_content_found');
      }
      return t('conditional_logic_selected_editor_section_title', {
        page: page.pageNum,
      });
    }

    return t('conditional_logic_no_content_found');
  };
}
